/**
 * (owner, Sep 17) Voice between players: the browser's microphone over WebRTC, peer to peer, one connection per player within
 * earshot, signalled through the game's own socket (`voice` messages the server relays by player id). What you hear of each
 * player is `hearing()` (src/game/voice-model.ts) applied every frame: a gain and a low-pass per peer, under the 'voice' bus of
 * the game's audio. The mic is push-to-talk (V held), a toggle (V, or the 🎙 on the corner bar), or off. docs/DESIGN_VOICE.md.
 *
 * The server never carries audio; it relays offers, answers and ICE candidates, and says who is talking (`tk` on the wire)
 * so everyone draws the rings. NAT: STUN only (a public STUN server) — two players both behind symmetric NATs will not
 * connect; a TURN server is the fix and a setting for later.
 */
import type { NetClient } from './client';
import type { Audio as GameAudio } from '../engine/audio';
import type { RenderState } from '../engine/render-state';
import type { NetOpts, VoiceSignal } from './protocol';
import { hearing, muffleHz, VOICE_RANGE, type Ear } from '../game/voice-model';

export type MicMode = 'off' | 'ptt' | 'toggle';
export interface VoiceUi {
  mode: MicMode;
  /** the mic is open now (held, or toggled on) */
  on: boolean;
  /** players in earshot of me right now — the rings: two while I talk, three when someone can hear it */
  heard: number;
  /** hear the players near me at all (the setting; off = every voice muted, calls apart) */
  hear: boolean;
  /** the voice bus level, 0..1.5 */
  volume: number;
  /** the mic could not be opened: what the browser said */
  error?: string;
  /** peers connected right now (the settings page shows it) */
  peers: number;
  /** the server offers a relay (TURN); how many of my links go through it */
  relay?: boolean; relayed?: number;
}
export const voiceUi: VoiceUi = { mode: 'ptt', on: false, heard: 0, hear: true, volume: 1, peers: 0 };
const STUN = { urls: 'stun:stun.l.google.com:19302' };
/** the ICE servers: STUN for the direct path, and the server's relay (TURN) when the panel has one on — a fallback only (`iceTransportPolicy` 'all': the relay carries a pair only when nothing direct connects) */
function iceConfig(opts: NetOpts): RTCConfiguration { const t = opts.turn; return { iceServers: t?.urls.length ? [STUN, { urls: t.urls, username: t.username, credential: t.credential }] : [STUN] }; }
/** keep a peer this long after it left earshot before hanging up (a car passing back and forth is not four connections) */
const LINGER = 6;

interface Peer {
  id: number; pc: RTCPeerConnection; polite: boolean; makingOffer: boolean; ignoreOffer: boolean;
  el?: HTMLAudioElement; src?: MediaStreamAudioSourceNode; filter?: BiquadFilterNode; gain?: GainNode; lastNear: number; connected: boolean;
  /** the link goes through the relay (TURN), read from the stats once connected */
  relay?: boolean;
  /** (calls) the peer's stream that is their phone's speaker — a call's far side, heard faintly by the players near them: its own source and gain */
  spkIds: Set<string>; spkSrc?: MediaStreamAudioSourceNode; spkGain?: GainNode; spkEl?: HTMLAudioElement;
  /** (calls) my speaker's track sent to this peer, to take back when the call ends */
  spkSender?: RTCRtpSender;
  /** the gain into my speaker mix: this peer is a call's far side */
  toSpk?: GainNode;
}
/** how loud a call's far side comes out of the phone's speaker for the players round the listener */
const SPEAKER = 0.35;

export class VoiceChat {
  private peers = new Map<number, Peer>();
  private mic?: MediaStream;
  private opening?: Promise<boolean>;
  private talking = false;
  private t = 0;
  constructor(private client: NetClient, private audio: GameAudio) {}

  /** the microphone, asked for on the first talk (the browser prompts once); false when refused */
  async openMic(): Promise<boolean> {
    if (this.mic) return true;
    if (this.opening) return this.opening;
    this.opening = (async () => {
      try {
        const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
        if (!md?.getUserMedia) { voiceUi.error = 'no microphone access in this browser'; return false; }
        this.mic = await md.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
        for (const tr of this.mic.getAudioTracks()) tr.enabled = this.talking;
        for (const p of this.peers.values()) for (const tr of this.mic.getAudioTracks()) p.pc.addTrack(tr, this.mic);
        voiceUi.error = undefined;
        return true;
      } catch (e) { voiceUi.error = e instanceof Error ? e.message : String(e); return false; }
      finally { this.opening = undefined; }
    })();
    return this.opening;
  }

  /** open or close the mic: the track is switched, the server told (the rings), nothing is sent while it is closed */
  setTalking(on: boolean): void {
    if (voiceUi.mode === 'off') on = false;
    if (on === this.talking) return;
    this.talking = on; voiceUi.on = on;
    if (on && !this.mic) { void this.openMic().then((ok) => { if (!ok) { this.talking = false; voiceUi.on = false; this.client.sendTalk(false); } }); }
    for (const tr of this.mic?.getAudioTracks() ?? []) tr.enabled = on;
    this.client.sendTalk(on);
  }
  get isTalking(): boolean { return this.talking; }

  /** every frame: the players within earshot get a connection, what each one sounds like from here is set, the ones long gone are hung up */
  update(state: RenderState, dt: number): void {
    this.t += dt;
    const me = state.player, myId = this.client.youId;
    const mine: Ear = { x: me.x, y: me.y, inside: me.inside, floor: me.floor, vehicle: me.vehicle?.id, windows: me.vehicle?.windows, dead: me.dead };
    let heard = 0;
    // (owner, Sep 17) the call I am on: its members are peers at full volume wherever they are — unless one side is on hold
    const cv = this.client.playerCalls?.(), call = cv?.call && !cv.call.held ? cv.call : undefined;
    const callIds = new Map<number, boolean>(); // id → held
    if (call) for (const m of call.members) if (m.id !== undefined && m.id !== myId) callIds.set(m.id, m.held);
    const buds = (me.items?.earbuds ?? 0) > 0; // Loop Buds in: the far side is for my ears only
    const seen = new Set<number>();
    for (const p of state.people) {
      if (!p.isPlayer || p.id === myId) continue;
      seen.add(p.id);
      const name = this.client.names.get(p.id), blocked = !!(name && this.client.blocks[name]?.voice);
      const d = Math.hypot(p.x - me.x, p.y - me.y);
      const near = !blocked && d < VOICE_RANGE * 1.25, onCall = callIds.has(p.id);
      let peer = this.peers.get(p.id);
      if ((near || onCall) && !peer && (voiceUi.hear || this.talking || onCall)) peer = this.peer(p.id);
      if (!peer) continue;
      if (near || onCall) peer.lastNear = this.t;
      const ear: Ear = { x: p.x, y: p.y, inside: p.inside, floor: p.floor, vehicle: p.vehicle?.id, windows: p.vehicle?.windows, dead: p.dead };
      const h = hearing(ear, mine);
      const line = onCall && !callIds.get(p.id) ? 1 : 0; // (the call line: clear, whatever the distance)
      const g = blocked ? 0 : Math.max(line, voiceUi.hear ? h.gain : 0) * voiceUi.volume;
      this.setLevel(peer, g, line ? 20000 : muffleHz(h.muffle));
      this.speakerLevel(peer, onCall ? (buds || callIds.get(p.id) ? 0 : 1) : 0, voiceUi.hear && !blocked ? h.gain * voiceUi.volume * SPEAKER : 0, near && !onCall);
      if (this.talking && (line || hearing(mine, ear).gain > 0.05) && peer.connected) heard++;
    }
    // call members out of the snapshot's range (170 tiles): peers all the same, at full volume
    for (const [id, held] of callIds) if (!seen.has(id)) { let peer = this.peers.get(id); if (!peer) peer = this.peer(id); peer.lastNear = this.t; this.setLevel(peer, held ? 0 : voiceUi.volume, 20000); this.speakerLevel(peer, buds || held ? 0 : 1, 0, false); }
    for (const [id, peer] of this.peers) if (this.t - peer.lastNear > LINGER) { this.client.sendVoice(id, { k: 'bye' }); this.close(peer); }
    voiceUi.heard = heard; voiceUi.peers = this.peers.size; voiceUi.relayed = [...this.peers.values()].filter((p) => p.relay).length; voiceUi.relay = !!this.client.opts.turn;
    me.talk = this.talking; me.talkHeard = heard;
  }

  /** a signal from another player, relayed by the server: the perfect-negotiation pattern — the higher id is polite */
  async signal(from: number, sig: VoiceSignal): Promise<void> {
    if (sig.k === 'bye') { const p = this.peers.get(from); if (p) this.close(p); return; }
    if (sig.k === 'spk') { const p = this.peers.get(from) ?? this.peer(from); p.spkIds.add(sig.s); return; }
    const peer = this.peers.get(from) ?? this.peer(from);
    const pc = peer.pc;
    try {
      if (sig.k === 'sdp') {
        const desc = sig.d;
        const collision = desc.type === 'offer' && (peer.makingOffer || pc.signalingState !== 'stable');
        peer.ignoreOffer = !peer.polite && collision;
        if (peer.ignoreOffer) return;
        await pc.setRemoteDescription(desc);
        if (desc.type === 'offer') { await pc.setLocalDescription(); this.client.sendVoice(from, { k: 'sdp', d: pc.localDescription!.toJSON() as RTCSessionDescriptionInit }); }
      } else if (sig.k === 'ice') {
        try { await pc.addIceCandidate(sig.c); } catch (e) { if (!peer.ignoreOffer) throw e; }
      }
    } catch (e) { console.warn('voice: signal failed', e); }
  }

  private peer(id: number): Peer {
    const pc = new RTCPeerConnection(iceConfig(this.client.opts));
    const peer: Peer = { id, pc, polite: this.client.youId > id, makingOffer: false, ignoreOffer: false, lastNear: this.t, connected: false, spkIds: new Set() };
    this.peers.set(id, peer);
    pc.onicecandidate = (e) => { if (e.candidate) this.client.sendVoice(id, { k: 'ice', c: e.candidate.toJSON() }); };
    pc.onnegotiationneeded = async () => {
      try { peer.makingOffer = true; await pc.setLocalDescription(); this.client.sendVoice(id, { k: 'sdp', d: pc.localDescription!.toJSON() as RTCSessionDescriptionInit }); }
      catch (e) { console.warn('voice: offer failed', e); }
      finally { peer.makingOffer = false; }
    };
    pc.onconnectionstatechange = () => { peer.connected = pc.connectionState === 'connected'; if (peer.connected) void this.readRelay(peer); else peer.relay = undefined; if (pc.connectionState === 'failed') { pc.restartIce(); } };
    pc.ontrack = (e) => { const st = e.streams[0] ?? new MediaStream([e.track]); if (peer.spkIds.has(st.id)) this.attachSpeaker(peer, st); else this.attach(peer, st); };
    if (this.mic) for (const tr of this.mic.getAudioTracks()) pc.addTrack(tr, this.mic);
    else if (!peer.polite) pc.addTransceiver('audio', { direction: 'recvonly' }); // (the impolite side opens the negotiation even before either mic is on, so the link is up when someone speaks)
    return peer;
  }

  /** the peer's voice into the game's audio: source → low-pass → gain → the 'voice' bus; an <audio> element consumes the stream too (Chrome needs one for WebRTC audio to flow) */
  private attach(peer: Peer, stream: MediaStream): void {
    if (typeof document !== 'undefined') { const el = peer.el ?? document.createElement('audio'); el.srcObject = stream; el.autoplay = true; el.muted = true; peer.el = el; void el.play().catch(() => undefined); }
    const bus = this.audio.voiceBus();
    if (!bus) return; // (no audio context yet: the element plays it unmuted until there is one)
    try {
      peer.src?.disconnect();
      const src = bus.ctx.createMediaStreamSource(stream), filter = peer.filter ?? bus.ctx.createBiquadFilter(), gain = peer.gain ?? bus.ctx.createGain();
      filter.type = 'lowpass'; if (!peer.filter) filter.frequency.value = 20000; if (!peer.gain) gain.gain.value = 0;
      src.connect(filter).connect(gain).connect(bus.bus);
      peer.src = src; peer.filter = filter; peer.gain = gain;
      if (peer.el) peer.el.muted = true;
    } catch (e) { console.warn('voice: attach failed', e); if (peer.el) peer.el.muted = false; }
  }
  /** my phone's speaker: the call's far sides mixed into one stream, sent to the players near me so they hear the call faintly (owner: "calls like real life — people near you hear you and the caller, unless you use Loop Buds") */
  private spkOut?: MediaStreamAudioDestinationNode;
  private speaker(): MediaStreamAudioDestinationNode | undefined { const bus = this.audio.voiceBus(); if (!bus) return undefined; if (!this.spkOut) this.spkOut = bus.ctx.createMediaStreamDestination(); return this.spkOut; }
  /** `into`: how much of this peer goes into my speaker mix (a call's far side: 1, else 0); `outGain`: how loud this peer's speaker stream plays here; `sendMine`: whether my speaker stream should reach this peer (a player near me who is not on the call) */
  private speakerLevel(peer: Peer, into: number, outGain: number, sendMine: boolean): void {
    const bus = this.audio.voiceBus(); if (!bus) return;
    const t = bus.ctx.currentTime;
    if (peer.src) { if (!peer.toSpk) { const spk = this.speaker(); if (spk) { peer.toSpk = bus.ctx.createGain(); peer.toSpk.gain.value = 0; peer.src.connect(peer.toSpk).connect(spk); } } if (peer.toSpk) peer.toSpk.gain.setTargetAtTime(into, t, 0.1); }
    if (peer.spkGain) peer.spkGain.gain.setTargetAtTime(outGain, t, 0.1);
    const spk = this.spkOut;
    if (sendMine && spk && into === 0 && !peer.spkSender) {
      const track = spk.stream.getAudioTracks()[0]; if (!track) return;
      this.client.sendVoice(peer.id, { k: 'spk', s: spk.stream.id }); // (the id goes first: the track's arrival must not read as a mic)
      try { peer.spkSender = peer.pc.addTrack(track, spk.stream); } catch (e) { console.warn('voice: speaker send failed', e); }
    } else if (!sendMine && peer.spkSender) { try { peer.pc.removeTrack(peer.spkSender); } catch { /* closed */ } peer.spkSender = undefined; }
  }
  /** a peer's speaker stream: its own source and gain into the voice bus, set by `speakerLevel` */
  private attachSpeaker(peer: Peer, stream: MediaStream): void {
    if (typeof document !== 'undefined') { const el = peer.spkEl ?? document.createElement('audio'); el.srcObject = stream; el.autoplay = true; el.muted = true; peer.spkEl = el; void el.play().catch(() => undefined); }
    const bus = this.audio.voiceBus(); if (!bus) return;
    try { peer.spkSrc?.disconnect(); const src = bus.ctx.createMediaStreamSource(stream), gain = peer.spkGain ?? bus.ctx.createGain(); if (!peer.spkGain) gain.gain.value = 0; src.connect(gain).connect(bus.bus); peer.spkSrc = src; peer.spkGain = gain; } catch (e) { console.warn('voice: speaker attach failed', e); }
  }
  private setLevel(peer: Peer, gain: number, hz: number): void {
    const bus = this.audio.voiceBus();
    if (peer.gain && peer.filter && bus) { const t = bus.ctx.currentTime; peer.gain.gain.setTargetAtTime(gain, t, 0.08); peer.filter.frequency.setTargetAtTime(hz, t, 0.1); }
    else if (peer.el) { peer.el.muted = false; peer.el.volume = Math.max(0, Math.min(1, gain)); } // (before the first tap unlocks the audio: the element, at the gain, no muffle)
    if (peer.el && bus && !peer.src && peer.el.srcObject) this.attach(peer, peer.el.srcObject as MediaStream); // (the context came after the stream: wire it now)
  }
  private close(peer: Peer): void {
    try { peer.pc.close(); } catch { /* closed */ }
    try { peer.src?.disconnect(); peer.gain?.disconnect(); peer.toSpk?.disconnect(); peer.spkSrc?.disconnect(); peer.spkGain?.disconnect(); } catch { /* gone */ }
    if (peer.spkEl) { peer.spkEl.pause(); peer.spkEl.srcObject = null; }
    if (peer.el) { peer.el.pause(); peer.el.srcObject = null; }
    this.peers.delete(peer.id);
  }
  /** for a test or the console: each peer's connection state and level */
  /** is this link relayed? the selected candidate pair's types, from the stats */
  private async readRelay(peer: Peer): Promise<void> {
    try { const st = await peer.pc.getStats(); let pair: { localCandidateId?: string; remoteCandidateId?: string } | undefined;
      st.forEach((r: { type: string; selected?: boolean; nominated?: boolean; state?: string; localCandidateId?: string; remoteCandidateId?: string }) => { if (r.type === 'candidate-pair' && (r.selected || (r.nominated && r.state === 'succeeded'))) pair = r; });
      if (!pair) return; const types = [pair.localCandidateId, pair.remoteCandidateId].map((id) => (id ? (st.get(id) as { candidateType?: string } | undefined)?.candidateType : undefined));
      peer.relay = types.includes('relay'); } catch { /* stats are best effort */ }
  }
  debug(): { id: number; state: RTCPeerConnectionState; relay?: boolean; gain: number; hz: number; polite: boolean; graph: boolean; el?: { muted: boolean; volume: number; paused: boolean }; ctx?: string }[] { const bus = this.audio.voiceBus(); return [...this.peers.values()].map((p) => ({ id: p.id, state: p.pc.connectionState, relay: p.relay, gain: p.gain?.gain.value ?? (p.el ? p.el.volume : 0), hz: p.filter?.frequency.value ?? 20000, polite: p.polite, graph: !!p.src, el: p.el ? { muted: p.el.muted, volume: p.el.volume, paused: p.el.paused } : undefined, ctx: bus ? `${bus.ctx.state} t=${bus.ctx.currentTime.toFixed(1)}` : 'none' })); }
  /** leaving the server: every link down, the mic released */
  dispose(): void {
    for (const p of [...this.peers.values()]) { this.client.sendVoice(p.id, { k: 'bye' }); this.close(p); }
    for (const tr of this.mic?.getAudioTracks() ?? []) tr.stop();
    this.mic = undefined; this.talking = false; voiceUi.on = false; voiceUi.heard = 0; voiceUi.peers = 0;
  }
}
